import React from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Phone } from "lucide-react";
import { ADJUSTABLE_FAQS, SITE } from "@/mock";

export function AdjustableHero() {
  return (
    <section
      data-testid="adjustable-hero"
      className="grid overflow-hidden bg-[#f3ebe0] md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
    >
      <picture className="block md:order-2">
        <source media="(max-width: 767px)" srcSet="/banners/mobile/adjustable-hero.webp" />
        <img
          src="/banners/adjustable-hero.webp"
          alt="Serta baselogic adjustable base with a raised head position in a modern bedroom"
          fetchPriority="high"
          className="aspect-[3/2] h-full w-full object-cover md:aspect-auto md:max-h-[26rem]"
          data-testid="adjustable-hero-image"
        />
      </picture>
      <div className="flex flex-col items-center justify-center px-6 py-9 text-center md:order-1 md:py-12">
        <p className="text-sm font-semibold uppercase tracking-[0.35em] text-[#9a6b4f] sm:text-base">
          Adjustable Bases
        </p>
        <span className="mt-4 h-px w-48 bg-[#c9a688] sm:w-64" aria-hidden="true" />
        <p className="mt-5 flex items-baseline gap-3 font-serif italic text-[#b5562c]" data-testid="adjustable-hero-offer">
          <span className="text-3xl sm:text-4xl">Save up to</span>
          <span className="text-5xl not-italic sm:text-6xl lg:text-7xl">$1,000</span>
        </p>
        <p className="mt-2 text-base uppercase tracking-[0.14em] text-[#b5562c] sm:text-lg">
          On Mattress + Adjustable Sets
        </p>
        <a
          href={SITE.phoneHref}
          data-testid="adjustable-hero-call"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#b5562c] px-7 py-3 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#93421f]"
        >
          <Phone className="h-4 w-4" aria-hidden="true" /> Call for Today&apos;s Price
        </a>
      </div>
    </section>
  );
}

const COPY = [
  {
    h: "Our Adjustable Bed Options",
    p: [
      "Take your sleep and relaxation to a whole new level with an adjustable bed from MattressTown. With a simple wireless remote you can raise the head and foot of your bed for sleeping, reading, watching TV or working on your laptop - and return to flat with one touch.",
      "Choose from the all-new Beautyrest Black Luxury Adjustable Base with Dual Action Back and Lumbar Support, King Koil DualFlex split-top bases that let each partner raise their own side, the SmartLife Adjustable Base and complete SmartLife sleep systems, plus Serta baselogic Gold and Platinum. Features include zero-gravity positioning, massage, anti-snore presets, under-bed lighting and USB charging. Pair any base with a Beautyrest, Serta, King Koil, Puffy or MLILY mattress and save more when you buy them together as a set.",
    ],
  },
  {
    h: "What Else You Should Know About Adjustable Beds",
    p: [
      "Most of the mattresses we carry are adjustable-base compatible, and our adjustable bases fit inside most standard bed frames, so you can keep the bedroom furniture you love. Couples can choose a split king or split queen so each side moves independently.",
      "Not sure which base is right for you? Visit our Fort Lauderdale showroom to test every position in person - our sleep experts will match you with the right mattress and base, then deliver and set it up for you.",
    ],
  },
];

export function AdjustableInfo() {
  return (
    <section className="mt-16 border-t border-neutral-200 pt-12" data-testid="adjustable-info">
      <div className="space-y-10">
        {COPY.map((c) => (
          <div key={c.h}>
            <h2 className="text-xl font-bold text-neutral-900 sm:text-2xl">{c.h}</h2>
            {c.p.map((t) => (
              <p key={t.slice(0, 24)} className="mt-3 leading-relaxed text-neutral-700">{t}</p>
            ))}
          </div>
        ))}
      </div>

      <div className="mx-auto mt-14 max-w-3xl">
        <h2 className="text-center text-2xl font-bold uppercase tracking-tight text-neutral-900 sm:text-3xl">FAQs</h2>
        <div className="mt-6 space-y-2.5">
          {ADJUSTABLE_FAQS.map((f, i) => (
            <details key={f.q} className="group rounded-lg bg-neutral-100 px-5 py-4" data-testid={`adjustable-faq-${i}`}>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-bold text-neutral-900 [&::-webkit-details-marker]:hidden">
                <span>{f.q}</span>
                <ChevronDown className="h-5 w-5 shrink-0 transition-transform group-open:rotate-180" aria-hidden="true" />
              </summary>
              <p className="mt-3 leading-relaxed text-neutral-600">{f.a}</p>
            </details>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/contact"
            data-testid="adjustable-contact-cta"
            className="inline-flex items-center rounded-full bg-[#D0021B] px-9 py-4 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#a80016]"
          >
            Talk to a Sleep Expert
          </Link>
        </div>
      </div>
    </section>
  );
}
